'use client';

import { PREVENTION_WAF_VENDORS } from '@terra/shared-contracts-integrations';
import {
  ApplicationPreventionSection,
  useApplicationPreventionRouting,
} from '@terra/ui-design-system';
import {
  useApplicationPreventionMappings,
  useApplicationWafSuggestions,
  useAwsCloudConnectionStatus,
  usePreventionMappingMutations,
  usePreventionWafTargets,
  useWafPreventionConnections,
} from '../../../hooks/api/useApplicationPreventionSettings';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useOpenWebAppPage } from '../../../hooks/useOpenWebAppPage';
import { WEB_APP_PATHS } from '../../../utils/webAppPaths';

const DISABLED_DISCOVERY = {
  vendor: PREVENTION_WAF_VENDORS.CLOUDFLARE,
  connectionId: undefined,
  enabled: false,
};

type TargetPreventionPanelProps = {
  applicationId: string;
  isReadOnly: boolean;
};

export function TargetPreventionPanel({
  applicationId,
  isReadOnly,
}: TargetPreventionPanelProps) {
  const openWebAppPage = useOpenWebAppPage();
  const { isFeatureFlagEnabled: isPreventionWafEnabled } = useFeatureFlags(
    'PREVENTION_WAF_ENABLED',
  );
  const { isFeatureFlagEnabled: isCloudAwsEnabled } =
    useFeatureFlags('CLOUD_AWS_ENABLED');
  const { connections, isLoading: isConnectionsLoading } =
    useWafPreventionConnections({ enabled: isPreventionWafEnabled });
  const { connectionStatus: awsCloudStatus } = useAwsCloudConnectionStatus({
    enabled: isCloudAwsEnabled && isPreventionWafEnabled,
  });
  const { mappings, refetch: refetchMappings } =
    useApplicationPreventionMappings({
      applicationId,
      enabled: isPreventionWafEnabled,
    });
  const { suggestions, isLoading: isSuggestionsLoading } =
    useApplicationWafSuggestions({
      applicationId,
      enabled: isPreventionWafEnabled,
    });
  const { createMapping, deleteMapping, ensureAwsWafConnection } =
    usePreventionMappingMutations({ applicationId });
  const routing = useApplicationPreventionRouting({
    applicationId,
    connections,
    mappings,
    suggestions,
    isAwsPreventionReady:
      isCloudAwsEnabled &&
      awsCloudStatus?.status === 'VALID' &&
      Boolean(awsCloudStatus.stagePreventionEnabled),
    onCreateMapping: createMapping.mutateAsync,
    onDeleteMapping: deleteMapping.mutateAsync,
    onEnsureAwsWafConnection: () => ensureAwsWafConnection.mutateAsync(),
    onRefetchMappings: refetchMappings,
  });
  const discovery = usePreventionWafTargets(
    routing.discoveryRequest ?? DISABLED_DISCOVERY,
  );

  if (!isPreventionWafEnabled) {
    return null;
  }

  return (
    <ApplicationPreventionSection
      routing={routing}
      discovery={{
        targets: discovery.targets,
        isLoading: discovery.isLoading,
        isFetching: discovery.isFetching,
        isError: discovery.isError,
        onRefresh: () => {
          void discovery.refetch();
        },
      }}
      isConnectionsLoading={isConnectionsLoading}
      isSuggestionsLoading={isSuggestionsLoading}
      isSaving={createMapping.isPending || ensureAwsWafConnection.isPending}
      isClearing={deleteMapping.isPending}
      isReadOnly={isReadOnly}
      onGoToIntegrations={() => openWebAppPage(WEB_APP_PATHS.integrations)}
    />
  );
}
